/**
 * Maps Firebase Auth UIDs to legacy membersDatabase document IDs for users
 * who were created before silent onboarding (Auth UID != membersDatabase doc ID).
 * Mapping is stored in Firestore and cached in localStorage (with in-memory use for lookups).
 *
 * Firestore: collection `authUidToMembersUid`, document `mappings`, field:
 *   mappings: { [authUid: string]: membersUid: string }
 * Example: { "Tvg4FMBXpbc77kZvW2DSGmBeoA43": "c976a295-7663-4a82-b163-378bef2f0c1c" }
 *
 * Use getEffectiveUserId(authUid) for all Firestore / profile lookups.
 */

import firebase from '../config/firebase.config';

const COLLECTION_NAME = 'authUidToMembersUid';
const MAP_DOC_ID = 'mappings';
const STORAGE_KEY = 'authUidToMembersUid_map';

/** In-memory cache: { [authUid]: membersUid }. Null until loaded. Used for fast lookups. */
let cachedMap = null;

/** Resolves when the cache has been loaded (or load failed). Used to avoid duplicate fetches. */
let loadPromise = null;

function readFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed))
      return parsed;
    return null;
  } catch {
    return null;
  }
}

function writeToStorage(map) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // ignore quota or disabled localStorage
  }
}

/**
 * Loads the authUid → membersUid map from localStorage (if present) or Firestore, then caches in both.
 * Safe to call multiple times; only the first call may fetch; later calls return the same promise.
 * @returns {Promise<void>}
 */
export function ensureLegacyMapLoaded() {
  if (cachedMap !== null) {
    return Promise.resolve();
  }
  const fromStorage = readFromStorage();
  if (fromStorage !== null) {
    cachedMap = fromStorage;
    return Promise.resolve();
  }
  if (loadPromise) {
    return loadPromise;
  }
  const db = firebase.firestore();
  loadPromise = db
    .collection(COLLECTION_NAME)
    .doc(MAP_DOC_ID)
    .get()
    .then((snap) => {
      if (
        snap.exists &&
        snap.data().mappings &&
        typeof snap.data().mappings === 'object'
      ) {
        cachedMap = { ...snap.data().mappings };
      } else {
        cachedMap = {};
      }
      writeToStorage(cachedMap);
    })
    .catch(() => {
      cachedMap = {};
    });
  return loadPromise;
}

/**
 * Returns the effective user ID for Firestore and app data.
 * For mapped users, returns the legacy membersDatabase doc ID; otherwise returns the Auth UID.
 * Uses localStorage-backed cache; call ensureLegacyMapLoaded() before first use (e.g. in auth sagas).
 * @param {string} authUid - Firebase Auth user UID
 * @returns {string} - UID to use for membersDatabase and all data queries
 */
export function getEffectiveUserId(authUid) {
  if (!authUid) return authUid;
  if (cachedMap === null) return authUid;
  return cachedMap[authUid] ?? authUid;
}
